"use client";

import { Suspense, useEffect, useState } from "react";
import LoadingLink from "@/components/navigation/LoadingLink";
import Logo from "../components/Logo";
import { useRouter, useSearchParams } from "next/navigation";
import { supabase } from "@/lib/supabaseClient";
import { getBrowserSiteOrigin } from "@/lib/siteUrl";

function LoginPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const fromVerification = searchParams.get("verified") === "1";
  const redirect = searchParams.get("redirect") || "/dashboard";
  const fromCheckout = redirect.includes("/checkout");

  useEffect(() => {
    if (searchParams.get("error") !== "oauth") return;
    const detail = searchParams.get("detail");
    setError(
      detail
        ? `Google sign-in failed: ${decodeURIComponent(detail)}`
        : "Google sign-in did not complete. Please try again.",
    );
    const params = new URLSearchParams(searchParams.toString());
    params.delete("error");
    params.delete("detail");
    const qs = params.toString();
    router.replace(`/login${qs ? `?${qs}` : ""}`, { scroll: false });
  }, [searchParams, router]);

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const getLoginErrorMessage = (message: string): string => {
    const msg = message.toLowerCase();
    if (
      msg.includes("invalid login") ||
      msg.includes("invalid email") ||
      msg.includes("invalid credentials") ||
      msg.includes("invalid password")
    ) {
      return "Incorrect email or password. Please check and try again.";
    }
    if (msg.includes("email not confirmed") || msg.includes("confirm")) {
      return "Please verify your email first. Check your inbox (and spam folder) for the confirmation link.";
    }
    if (msg.includes("too many") || msg.includes("rate limit") || msg.includes("attempts")) {
      return "Too many login attempts. Please wait a few minutes and try again.";
    }
    if (msg.includes("user not found")) {
      return "No account found with this email. Please sign up first.";
    }
    return message;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      setError("Please enter your email address.");
      return;
    }
    if (!isValidEmail(emailTrimmed)) {
      setError("Please enter a valid email address (e.g. name@example.com).");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    try {
      setLoading(true);
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: emailTrimmed,
        password,
      });

      if (signInError) {
        setError(getLoginErrorMessage(signInError.message));
        setLoading(false);
        return;
      }

      router.push(redirect);
    } catch (cause) {
      // eslint-disable-next-line no-console
      console.error(cause);
      setError("We couldn’t log you in right now. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    if (typeof window === "undefined") return;

    const safePath =
      redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : "/dashboard";
    const callback = new URL(`${getBrowserSiteOrigin()}/auth/callback`);
    callback.searchParams.set("next", safePath);

    try {
      setOauthLoading(true);
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: callback.toString(),
          queryParams: { prompt: "select_account" },
          skipBrowserRedirect: true,
        },
      });

      if (oauthError) {
        // eslint-disable-next-line no-console
        console.error("Google OAuth error:", oauthError.message);
        const msg = oauthError.message.toLowerCase();
        if (msg.includes("popup") || msg.includes("closed")) {
          setError("Sign-in was cancelled or the window was closed. Please try again.");
        } else if (msg.includes("email") && msg.includes("already")) {
          setError("This email is already registered. Please log in with your password.");
        } else {
          setError(oauthError.message);
        }
        setOauthLoading(false);
        return;
      }

      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      setError("Could not start Google sign-in. Check that the Google provider is enabled in Supabase.");
    } catch (cause) {
      // eslint-disable-next-line no-console
      console.error(cause);
      setError("We couldn’t connect to Google. Please try again or use email and password.");
    }
    setOauthLoading(false);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10 text-slate-100 sm:px-6">
      <div className="absolute left-4 top-6 sm:left-6">
        <Logo href="/" className="opacity-90 hover:opacity-100" />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-300">
            Welcome back
          </p>
          <h1 className="mt-2 font-heading text-2xl font-semibold text-slate-50">
            Log in to Aptinomic
          </h1>
          <p className="mt-1 text-xs text-slate-300">
            {fromCheckout
              ? "Sign in to continue to the payment page."
              : "Continue your AI-guided career exploration."}
          </p>
        </div>

        <div className="relative z-10 rounded-3xl bg-slate-950/90 p-6 shadow-xl shadow-slate-950/60 ring-1 ring-slate-800">
          {fromVerification && (
            <p className="mb-3 rounded-2xl bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-200">
              Email verified successfully. You can now log in.
            </p>
          )}

          {resetMessage && (
            <p className="mb-3 rounded-2xl bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
              {resetMessage}
            </p>
          )}

          {error && (
            <p className="mb-3 rounded-2xl bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {error}
            </p>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="text-xs font-medium text-slate-200"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-3 py-2.5 text-sm text-slate-100 outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="text-xs font-medium text-slate-200"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-3 py-2.5 text-sm text-slate-100 outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                placeholder="Your password"
              />
              <div className="mt-1 text-right">
                <button
                  type="button"
                  className="text-[11px] font-semibold text-amber-300 hover:text-amber-200"
                  onClick={async () => {
                    const emailTrimmed = email.trim();
                    if (!emailTrimmed) {
                      setError("Please enter your email above, then click Forgot password? to receive a reset link.");
                      setResetMessage(null);
                      return;
                    }
                    if (!isValidEmail(emailTrimmed)) {
                      setError("Please enter a valid email address to reset your password.");
                      setResetMessage(null);
                      return;
                    }
                    setError(null);
                    setResetMessage(null);
                    try {
                      setLoading(true);
                      const redirectTo =
                        typeof window !== "undefined"
                          ? `${getBrowserSiteOrigin()}/auth/reset-password`
                          : undefined;
                      const { error: resetError } =
                        await supabase.auth.resetPasswordForEmail(
                          emailTrimmed,
                          {
                            redirectTo,
                          },
                        );
                      if (resetError) {
                        const m = resetError.message.toLowerCase();
                        if (m.includes("rate limit") || m.includes("too many")) {
                          setError("Too many reset requests. Please wait a few minutes and try again.");
                        } else if (m.includes("not found") || m.includes("no user")) {
                          setError("No account found with this email. Please check the address or sign up.");
                        } else {
                          setError(resetError.message);
                        }
                        return;
                      }
                      setResetMessage("Password reset link sent! Check your email (and spam folder) for instructions.");
                    } catch (cause) {
                      // eslint-disable-next-line no-console
                      console.error(cause);
                      setError("We couldn’t send the reset link. Please try again in a moment.");
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center rounded-full bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-md shadow-amber-500/40 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-amber-300/70"
            >
              {loading ? "Logging you in..." : "Log in"}
            </button>
          </form>

          <div className="mt-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-800" />
            <span className="text-[11px] text-slate-400">
              Or continue with
            </span>
            <div className="h-px flex-1 bg-slate-800" />
          </div>

          <button
            type="button"
            onClick={() => void handleGoogleLogin()}
            disabled={oauthLoading}
            className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-slate-700 bg-slate-950/80 px-4 py-2.5 text-sm font-semibold text-slate-100 shadow-sm transition hover:border-amber-300 hover:text-amber-200 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white">
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4">
                <path
                  fill="#EA4335"
                  d="M12 10.2v3.9h5.4c-.2 1.3-1.5 3.9-5.4 3.9-3.2 0-5.9-2.7-5.9-6s2.7-6 5.9-6c1.8 0 3 .8 3.7 1.5l2.5-2.4C16.6 3.6 14.5 2.7 12 2.7 6.9 2.7 2.8 6.8 2.8 12s4.1 9.3 9.2 9.3c5.3 0 8.8-3.7 8.8-8.9 0-.6-.1-1-.1-1.4H12z"
                />
                <path
                  fill="#34A853"
                  d="M2.8 7.1l3.2 2.3c.8-2.2 3-3.8 6-3.8 1.8 0 3 .8 3.7 1.5l2.5-2.4C16.6 3.6 14.5 2.7 12 2.7 8.4 2.7 5.2 4.7 3.6 7.7l-.8-.6z"
                />
                <path
                  fill="#4285F4"
                  d="M12 21.3c2.4 0 4.5-.8 6-2.3l-2.8-2.2c-.7.5-1.8 1.1-3.2 1.1-3 0-5.3-2-6.2-4.7L2.6 15c1.6 3.8 5.2 6.3 9.4 6.3z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.8 13.2c-.2-.5-.3-1-.3-1.6s.1-1.1.3-1.6L2.6 7.7C2 9 1.7 10.4 1.7 11.9s.3 2.9.9 4.2l3.2-2.9z"
                />
              </svg>
            </span>
            {oauthLoading ? "Connecting to Google..." : "Continue with Google"}
          </button>

          <p className="mt-4 text-center text-[11px] text-slate-400">
            New to Aptinomic?{" "}
            <LoadingLink
              href="/signup"
              className="font-semibold text-amber-300 hover:text-amber-200"
            >
              Create an account
            </LoadingLink>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-slate-950" />}>
      <LoginPageInner />
    </Suspense>
  );
}

